import { Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Typography } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import {
    Add as AddIcon,
    Badge as BadgeIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    CloudDownload as CloudDownloadIcon,
    CloudUpload as CloudUploadIcon,
    Close as CloseIcon,
} from "@mui/icons-material"
import { useCallback, useEffect, useState } from "react"

import { membershipApi } from "@/services/api"
import { format as formatDateFns } from 'date-fns'
import toast from "react-hot-toast"

interface License {
    _id: string
    therapistId: string
    licenseState: string
    licenseNumber: string
    licenseType: string
    licenseNpi: string
    issueDate?: string
    expiryDate: string
    licenseStatus: string
    licenseFileUrl: string
    isDeleted: boolean
    createdAt: string
    updatedAt: string
}
interface LicenseType {
    _id: string
    name: string
    description: string
    isDeleted: boolean
    required?: 'mandatory' | 'non_mandatory'
}

interface LicensesResponse {
    message: string
    currentPage: string
    totalRecords: number
    totalPages: number
    limit: string
    data: License[]
}


const LicenseManagementPage: React.FC = () => {
    const [addLicenseDialogOpen, setAddLicenseDialogOpen] = useState(false)
    const [isLoadingLicenses, setIsLoadingLicenses] = useState(false)
    const [licenses, setLicenses] = useState<License[]>([])
    const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([])
    const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null)
    const [licenseFiles, setLicenseFiles] = useState<Map<string, File>>(new Map())
    const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [isLoadingLicenseTypes, setIsLoadingLicenseTypes] = useState(false)
    const [states, setStates] = useState<Array<{ name: string; code: string }>>(
        []
    )
    const [newLicense, setNewLicense] = useState({
        licenseType: '',
        licenseNpi: '',
        licenseState: '',
        licenseNumber: '',
        issueDate: '',
        expiryDate: '',
        licenseStatus: 'Active',
        licenseFileUrl: '',
    })
    const [newLicenseFile, setNewLicenseFile] = useState<File | null>(null)
    const handleOpenAddLicenseDialog = () => {
        setAddLicenseDialogOpen(true)
    }

    const handleCloseAddLicenseDialog = () => {
        setAddLicenseDialogOpen(false)
        // Clean up preview URL if it exists
        if (
            newLicense.licenseFileUrl &&
            newLicense.licenseFileUrl.startsWith('blob:')
        ) {
            URL.revokeObjectURL(newLicense.licenseFileUrl)
        }
        // Reset form
        setNewLicense({
            licenseType: '',
            licenseNpi: '',
            licenseState: '',
            licenseNumber: '',
            issueDate: '',
            expiryDate: '',
            licenseStatus: 'Active',
            licenseFileUrl: '',
        })
        setNewLicenseFile(null)
    }
    const isNpiRequired = (licenseTypeId: string): boolean => {
        const licenseType = licenseTypes.find(lt => lt._id === licenseTypeId)
        return licenseType?.required === 'mandatory'
    }

    // Fetch licenses
    const loadLicenses = useCallback(async () => {
        setIsLoadingLicenses(true)
        try {
            const response = await membershipApi.getLicensesAdmin(1, 10)
            const licensesData = (response as LicensesResponse).data || []
            // Filter out deleted licenses
            const activeLicenses = licensesData.filter(license => !license.isDeleted)
            setLicenses(activeLicenses)
        } catch (e) {
            console.error('Failed to load licenses', e)
            toast.error('Failed to load licenses')
        } finally {
            setIsLoadingLicenses(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useEffect(() => {
        loadLicenses()
    }, [loadLicenses])

    const handleSaveLicense = async (license: License) => {
        try {
            // Validate NPI if required
            if (license.licenseType !== '69aacb7782693bdedf50db43') {
                if (!license.licenseType) {
                    toast.error('License Type is required')
                    return
                }
                if (!license.licenseNumber) {
                    toast.error('License number is required')
                    return
                }
                if (!license.licenseState) {
                    toast.error('License State is required')
                    return
                }
                if (!license.licenseNumber) {
                    toast.error('License Number is required')
                    return
                }
                if (!license.issueDate) {
                    toast.error('Issue Date is required')
                    return
                }
                if (!license.expiryDate) {
                    toast.error('Expiry Date is required')
                    return
                }
                //expri date must be geter then issue date
                if (license.issueDate && license.expiryDate) {
                    const issue = new Date(license.issueDate)
                    const expiry = new Date(license.expiryDate)
                    if (expiry <= issue) {
                        toast.error('Expiry Date must be after Issue Date')
                        return
                    }
                }
                if (!license.licenseStatus) {
                    toast.error('License Status is required')
                    return
                }
                if (!license) {
                    toast.error('License File is required')
                    return
                }
            }




            const formData = new FormData()
            formData.append('licenseType', license.licenseType)
            formData.append('licenseNpi', license.licenseNpi || '')
            formData.append('licenseState', license.licenseState)
            formData.append('licenseNumber', license.licenseNumber)
            if (license.issueDate) {
                formData.append('issueDate', license.issueDate)
            }
            formData.append('expiryDate', license.expiryDate)
            formData.append('licenseStatus', license.licenseStatus)

            // Include the file if one was selected for this license
            const fileToUpload = licenseFiles.get(license._id)
            if (fileToUpload) {
                formData.append('file', fileToUpload)
            }

            await membershipApi.updateLicenseAdmin(license._id, formData)
            toast.success('License updated successfully')

            // Clean up: remove the file from state and revoke object URL if it was a preview
            const fileToCleanup = licenseFiles.get(license._id)
            if (
                fileToCleanup &&
                license.licenseFileUrl &&
                license.licenseFileUrl.startsWith('blob:')
            ) {
                URL.revokeObjectURL(license.licenseFileUrl)
            }
            setLicenseFiles(prev => {
                const newMap = new Map(prev)
                newMap.delete(license._id)
                return newMap
            })

            setEditingLicenseId(null)

            // Reload licenses to get fresh data
            loadLicenses()
        } catch (e) {
            console.error('Failed to save license', e)
            toast.error('Failed to save license')
        }
    }

    const handleCancelEdit = () => {
        // Clean up any files that were selected but not saved
        const licenseId = editingLicenseId
        if (licenseId) {
            const license = licenses.find(lic => lic._id === licenseId)
            if (license) {
                // Check if we have a file for this license that was just selected
                const file = licenseFiles.get(licenseId)
                if (file && license.licenseFileUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(license.licenseFileUrl)
                    // Restore original URL if it exists (from previous save)
                    // For now, we'll just remove the file from state
                    setLicenseFiles(prev => {
                        const newMap = new Map(prev)
                        newMap.delete(licenseId)
                        return newMap
                    })
                    // Reload to get original license data back
                    loadLicenses()
                }
            }
        }
        setEditingLicenseId(null)
    }

    const handleEditLicense = (licenseId: string) => {
        setEditingLicenseId(licenseId)
    }
    const handleDeleteLicenseClick = (license: License) => {
        setLicenseToDelete(license)
        setDeleteDialogOpen(true)
    }

    const handleLicenseFieldChange = (
        licenseId: string,
        field: keyof License,
        value: string
    ) => {
        setLicenses(prev =>
            prev.map(lic =>
                lic._id === licenseId ? { ...lic, [field]: value } : lic
            )
        )
    }

    const handleLicenseFileUpload = (licenseId: string, file: File | null) => {
        if (!file) return
        // Store the file object to be included in FormData when saving
        setLicenseFiles(prev => {
            const newMap = new Map(prev)
            newMap.set(licenseId, file)
            return newMap
        })
        // Create a preview URL for display purposes
        const previewUrl = URL.createObjectURL(file)
        setLicenses(prev =>
            prev.map(lic =>
                lic._id === licenseId ? { ...lic, licenseFileUrl: previewUrl } : lic
            )
        )
        toast.success(
            'License document selected. Click Save to upload.'
        )
    }

    const handleNewLicenseChange = (field: string, value: string) => {
        setNewLicense(prev => ({ ...prev, [field]: value }))
    }

    const handleNewLicenseFileUpload = (file: File | null) => {
        if (!file) return
        // Store the file object to be included in FormData when adding
        setNewLicenseFile(file)
        // Create a preview URL for display purposes
        const previewUrl = URL.createObjectURL(file)
        setNewLicense(prev => ({ ...prev, licenseFileUrl: previewUrl }))
        toast.success('License document selected. Click Add to upload.')
    }

    const handleAddLicense = async () => {
        try {
            // Validate NPI if required
            if (newLicense.licenseType !== '69aacb7782693bdedf50db43') {
                if (
                    isNpiRequired(newLicense.licenseType) &&
                    !newLicense.licenseNpi?.trim()
                ) {
                    toast.error('NPI Number is required for this license type')
                    return
                }
                if (!newLicense.licenseState) {
                    toast.error('License State is required')
                    return
                }
                if (!newLicense.licenseNumber) {
                    toast.error('License Number is required')
                    return
                }
                if (!newLicense.issueDate) {
                    toast.error('Issue Date is required')
                    return
                }
                if (!newLicense.expiryDate) {
                    toast.error('Expiry Date is required')
                    return
                }
                //expri date must be geter then issue date
                if (newLicense.issueDate && newLicense.expiryDate) {
                    const issue = new Date(newLicense.issueDate)
                    const expiry = new Date(newLicense.expiryDate)
                    if (expiry <= issue) {
                        toast.error('Expiry Date must be after Issue Date')
                        return
                    }
                }
                if (!newLicense.licenseStatus) {
                    toast.error('License Status is required')
                    return
                }
                if (!newLicenseFile) {
                    toast.error('License File is required')
                    return
                }
            }

            const formData = new FormData()
            formData.append('licenseType', newLicense.licenseType)
            formData.append('licenseNpi', newLicense.licenseNpi || '')
            formData.append('licenseState', newLicense.licenseState)
            formData.append('licenseNumber', newLicense.licenseNumber)
            if (newLicense.issueDate) {
                formData.append('issueDate', newLicense.issueDate)
            }
            formData.append('expiryDate', newLicense.expiryDate)
            formData.append('licenseStatus', newLicense.licenseStatus)

            // Include the file if one was selected
            if (newLicenseFile) {
                formData.append('file', newLicenseFile)
            }

            await membershipApi.addLicenseCertificate(formData)
            toast.success('License added successfully')
            handleCloseAddLicenseDialog()
            // Reload licenses to get fresh data
            loadLicenses()
        } catch (e) {
            console.error('Failed to add license', e)
            toast.error('Failed to add license')
        }
    }

    useEffect(() => {
        const loadStates = async () => {
            try {
                const response = await membershipApi.getStates()
                const statesData = response.data || response || []
                setStates(statesData)
            } catch (e) {
                console.error('Failed to load states', e)
                toast.error('Failed to load states')
            }
        }
        loadStates()

        const loadLicenseTypes = async () => {
            try {
                setIsLoadingLicenseTypes(true)
                const response = await membershipApi.getSpecialtyRoles()
                const licenseTypesData = response.data || response || []
                if (Array.isArray(licenseTypesData)) {
                    setLicenseTypes(
                        licenseTypesData.filter((lt: LicenseType) => !lt.isDeleted)
                    )
                }
            } catch (e) {
                console.error('Failed to load license types', e)
            } finally {
                setIsLoadingLicenseTypes(false)
            }
        }
        loadLicenseTypes()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleConfirmDelete = async () => {
        if (!licenseToDelete) return
        try {
            await membershipApi.deleteLicenseAdmin(licenseToDelete._id)
            toast.success('License deleted successfully')
            setDeleteDialogOpen(false)
            setLicenseToDelete(null)
            // Reload licenses
            loadLicenses()
        } catch (e) {
            console.error('Failed to delete license', e)
            toast.error('Failed to delete license')
        }
    }

    return (
        <Grid item xs={12}>
            <Card>
                <CardContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: 'grey.800',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <BadgeIcon sx={{ color: 'primary.main' }} />
                            License Information
                        </Typography>
                        {licenses.length === 0 && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAddLicenseDialog}
                                sx={{ textTransform: 'none' }}
                            >
                                Add License
                            </Button>
                        )}
                    </Box>

                    {isLoadingLicenses ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : licenses.length === 0 ? (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 4,
                                color: 'grey.500',
                            }}
                        >
                            <Typography variant="body1">
                                No licenses found. Please add your license information.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {licenses.map(license => {
                                const isEditing = editingLicenseId === license._id
                                return (
                                    <Grid item xs={12} key={license._id}>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                border: '1px solid',
                                                borderColor: 'grey.200',
                                                borderRadius: 2,
                                                bgcolor: 'grey.50',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    mb: 3,
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: 'grey.800',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        {/* {license.licenseName || license.licenseType} */}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {isEditing ? (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<SaveIcon />}
                                                                onClick={() => handleSaveLicense(license)}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={handleCancelEdit}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<EditIcon />}
                                                                onClick={() =>
                                                                    handleEditLicense(license._id)
                                                                }
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="error"
                                                                startIcon={<DeleteIcon fontSize="small" />}
                                                                onClick={() =>
                                                                    handleDeleteLicenseClick(license)
                                                                }
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>

                                            <Grid container spacing={2}>
                                                {/* Specialty License Type */}
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl
                                                        fullWidth
                                                        disabled={!isEditing || isLoadingLicenseTypes}
                                                    >
                                                        <InputLabel>
                                                            Specialty License Type *
                                                        </InputLabel>
                                                        <Select
                                                            value={license.licenseType}
                                                            onChange={e =>
                                                                handleLicenseFieldChange(
                                                                    license._id,
                                                                    'licenseType',
                                                                    e.target.value
                                                                )
                                                            }
                                                            label="Specialty License Type *"
                                                        >
                                                            {isLoadingLicenseTypes ? (
                                                                <MenuItem disabled>Loading...</MenuItem>
                                                            ) : (
                                                                licenseTypes.map(licenseType => (
                                                                    <MenuItem
                                                                        key={licenseType._id}
                                                                        value={licenseType._id}
                                                                    >
                                                                        {licenseType.name}
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                {license.licenseType !== '69aacb7782693bdedf50db43' && (
                                                    <>
                                                        {/* NPI Number */}
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                label={
                                                                    isNpiRequired(license.licenseType)
                                                                        ? 'NPI Number'
                                                                        : 'NPI Number'
                                                                }
                                                                value={license.licenseNpi || ''}
                                                                onChange={e =>
                                                                    handleLicenseFieldChange(
                                                                        license._id,
                                                                        'licenseNpi',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                fullWidth
                                                                disabled={!isEditing}
                                                                required={isNpiRequired(license.licenseType)}
                                                                error={
                                                                    isEditing &&
                                                                    isNpiRequired(license.licenseType) &&
                                                                    !license.licenseNpi?.trim()
                                                                }
                                                                helperText={
                                                                    isEditing &&
                                                                        isNpiRequired(license.licenseType) &&
                                                                        !license.licenseNpi?.trim()
                                                                        ? 'NPI Number is required for this license type'
                                                                        : ''
                                                                }
                                                            />
                                                        </Grid>

                                                        {/* License State */}
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControl fullWidth disabled={!isEditing}>
                                                                <InputLabel>License State *</InputLabel>
                                                                <Select
                                                                    value={license.licenseState}
                                                                    onChange={e =>
                                                                        handleLicenseFieldChange(
                                                                            license._id,
                                                                            'licenseState',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    label="License State *"
                                                                >
                                                                    {states.map(state => (
                                                                        <MenuItem
                                                                            key={state.code || state.name}
                                                                            value={state.name}
                                                                        >
                                                                            {state.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>

                                                        {/* License Number */}
                                                        <Grid item xs={12} sm={6}>
                                                            <TextField
                                                                label="License Number *"
                                                                value={license.licenseNumber}
                                                                onChange={e =>
                                                                    handleLicenseFieldChange(
                                                                        license._id,
                                                                        'licenseNumber',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                fullWidth
                                                                disabled={!isEditing}
                                                            />
                                                        </Grid>

                                                        {/* License Issue Date */}
                                                        <Grid item xs={12} sm={6}>
                                                            <LocalizationProvider
                                                                dateAdapter={AdapterDateFns}
                                                            >
                                                                <DatePicker
                                                                    label="License Issue Date *"
                                                                    value={
                                                                        license.issueDate
                                                                            ? new Date(license.issueDate)
                                                                            : null
                                                                    }
                                                                    onChange={value => {
                                                                        const formatted = value
                                                                            ? formatDateFns(
                                                                                value as Date,
                                                                                'MM/dd/yyyy'
                                                                            )
                                                                            : ''
                                                                        handleLicenseFieldChange(
                                                                            license._id,
                                                                            'issueDate',
                                                                            formatted
                                                                        )
                                                                    }}
                                                                    format="MM/dd/yyyy"
                                                                    views={['year', 'month', 'day']}
                                                                    openTo="year"
                                                                    maxDate={new Date()}
                                                                    disabled={!isEditing}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            placeholder: 'MM/DD/YYYY',
                                                                        },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>

                                                        {/* License Expiration Date */}
                                                        <Grid item xs={12} sm={6}>
                                                            <LocalizationProvider
                                                                dateAdapter={AdapterDateFns}
                                                            >
                                                                <DatePicker
                                                                    label="License Expiration Date *"
                                                                    value={
                                                                        license.expiryDate
                                                                            ? new Date(license.expiryDate)
                                                                            : null
                                                                    }
                                                                    onChange={value => {
                                                                        const formatted = value
                                                                            ? formatDateFns(
                                                                                value as Date,
                                                                                'MM/dd/yyyy'
                                                                            )
                                                                            : ''
                                                                        handleLicenseFieldChange(
                                                                            license._id,
                                                                            'expiryDate',
                                                                            formatted
                                                                        )
                                                                    }}
                                                                    format="MM/dd/yyyy"
                                                                    views={['year', 'month', 'day']}
                                                                    openTo="year"
                                                                    disabled={!isEditing}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            placeholder: 'MM/DD/YYYY',
                                                                        },
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>

                                                        {/* License Status */}
                                                        <Grid item xs={12} sm={6}>
                                                            <FormControl fullWidth disabled={!isEditing}>
                                                                <InputLabel>License Status *</InputLabel>
                                                                <Select
                                                                    value={license.licenseStatus}
                                                                    onChange={e =>
                                                                        handleLicenseFieldChange(
                                                                            license._id,
                                                                            'licenseStatus',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    label="License Status *"
                                                                >
                                                                    <MenuItem value="Active">Active</MenuItem>
                                                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                                                    <MenuItem value="Suspended">
                                                                        Suspended
                                                                    </MenuItem>
                                                                    <MenuItem value="Expired">Expired</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                    </>
                                                )}

                                                {/* Upload License Document */}
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mb: 1, fontWeight: 500 }}
                                                    >
                                                        Upload License Document {license.licenseType !== '69aacb7782693bdedf50db43' ? '*' : ''}
                                                    </Typography>
                                                    {license.licenseFileUrl ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                href={license.licenseFileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                View Document
                                                            </Button>
                                                            {isEditing && (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    component="label"
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Replace Document
                                                                    <input
                                                                        type="file"
                                                                        hidden
                                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                                        onChange={e =>
                                                                            handleLicenseFileUpload(
                                                                                license._id,
                                                                                e.target.files?.[0] || null
                                                                            )
                                                                        }
                                                                    />
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                border: '2px dashed',
                                                                borderColor: 'grey.300',
                                                                borderRadius: 1,
                                                                p: 3,
                                                                textAlign: 'center',
                                                                bgcolor: 'background.paper',
                                                            }}
                                                        >
                                                            <CloudUploadIcon
                                                                sx={{
                                                                    fontSize: 48,
                                                                    color: 'primary.main',
                                                                    mb: 1,
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                Drag and drop your license document, or
                                                                click to browse
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block', mt: 0.5 }}
                                                            >
                                                                Supported formats: PDF, JPG, PNG (Max 10MB)
                                                            </Typography>
                                                            {isEditing && (
                                                                <Button
                                                                    component="label"
                                                                    variant="text"
                                                                    sx={{ mt: 1, textTransform: 'none' }}
                                                                >
                                                                    browse
                                                                    <input
                                                                        type="file"
                                                                        hidden
                                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                                        onChange={e =>
                                                                            handleLicenseFileUpload(
                                                                                license._id,
                                                                                e.target.files?.[0] || null
                                                                            )
                                                                        }
                                                                    />
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    )}
                </CardContent>
            </Card>
            <Dialog
                open={addLicenseDialogOpen}
                onClose={handleCloseAddLicenseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Add New License
                        </Typography>
                        <IconButton onClick={handleCloseAddLicenseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={3}>
                        {/* Specialty License Type */}
                        <Grid item xs={12} sm={6}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                            >
                                Specialty License Type *
                            </Typography>
                            <FormControl fullWidth disabled={isLoadingLicenseTypes}>
                                <Select
                                    value={newLicense.licenseType}
                                    onChange={e =>
                                        handleNewLicenseChange('licenseType', e.target.value)
                                    }
                                    displayEmpty
                                    sx={{
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey.300',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'grey.400',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#2E7DAF',
                                            borderWidth: 2,
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>
                                            {isLoadingLicenseTypes
                                                ? 'Loading license types...'
                                                : 'Select license type'}
                                        </em>
                                    </MenuItem>
                                    {licenseTypes.map(licenseType => (
                                        <MenuItem key={licenseType._id} value={licenseType._id}>
                                            {licenseType.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {newLicense.licenseType !== '69aacb7782693bdedf50db43' && (
                            <>
                                {/* NPI Number */}
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        NPI Number{isNpiRequired(newLicense.licenseType) ? ' *' : ''}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter NPI number"
                                        value={newLicense.licenseNpi || ''}
                                        onChange={e =>
                                            handleNewLicenseChange('licenseNpi', e.target.value)
                                        }
                                        required={isNpiRequired(newLicense.licenseType)}
                                        error={
                                            isNpiRequired(newLicense.licenseType) &&
                                            !newLicense.licenseNpi?.trim()
                                        }
                                        helperText={
                                            isNpiRequired(newLicense.licenseType) &&
                                                !newLicense.licenseNpi?.trim()
                                                ? 'NPI Number is required for this license type'
                                                : ''
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'white',
                                                borderRadius: 2,
                                                '& fieldset': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* License State */}
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License State *
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={newLicense.licenseState}
                                            onChange={e =>
                                                handleNewLicenseChange('licenseState', e.target.value)
                                            }
                                            displayEmpty
                                            sx={{
                                                borderRadius: 2,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>Select state</em>
                                            </MenuItem>
                                            {states.map(state => (
                                                <MenuItem
                                                    key={state.code || state.name}
                                                    value={state.name}
                                                >
                                                    {state.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* License Number */}
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Number *
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter license number"
                                        value={newLicense.licenseNumber}
                                        onChange={e =>
                                            handleNewLicenseChange('licenseNumber', e.target.value)
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'white',
                                                borderRadius: 2,
                                                '& fieldset': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* License Issue Date */}
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Issue Date *
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            value={
                                                newLicense.issueDate
                                                    ? new Date(newLicense.issueDate)
                                                    : null
                                            }
                                            onChange={value => {
                                                const formatted = value
                                                    ? formatDateFns(value as Date, 'MM/dd/yyyy')
                                                    : ''
                                                handleNewLicenseChange('issueDate', formatted)
                                            }}
                                            format="MM/dd/yyyy"
                                            views={['year', 'month', 'day']}
                                            openTo="year"
                                            maxDate={new Date()}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    placeholder: 'MM/DD/YYYY',
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: 'white',
                                                            borderRadius: 2,
                                                            '& fieldset': {
                                                                borderColor: 'grey.300',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey.400',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#2E7DAF',
                                                                borderWidth: 2,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                {/* License Expiration Date */}
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Expiration Date *
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            value={
                                                newLicense.expiryDate
                                                    ? new Date(newLicense.expiryDate)
                                                    : null
                                            }
                                            onChange={value => {
                                                const formatted = value
                                                    ? formatDateFns(value as Date, 'MM/dd/yyyy')
                                                    : ''
                                                handleNewLicenseChange('expiryDate', formatted)
                                            }}
                                            format="MM/dd/yyyy"
                                            views={['year', 'month', 'day']}
                                            openTo="year"
                                            minDate={new Date()}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    placeholder: 'MM/DD/YYYY',
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            bgcolor: 'white',
                                                            borderRadius: 2,
                                                            '& fieldset': {
                                                                borderColor: 'grey.300',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey.400',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#2E7DAF',
                                                                borderWidth: 2,
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                {/* License Status */}
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                                    >
                                        License Status *
                                    </Typography>
                                    <FormControl fullWidth>
                                        <Select
                                            value={newLicense.licenseStatus || 'Active'}
                                            onChange={e =>
                                                handleNewLicenseChange('licenseStatus', e.target.value)
                                            }
                                            sx={{
                                                borderRadius: 2,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'grey.300',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'grey.400',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#2E7DAF',
                                                    borderWidth: 2,
                                                },
                                            }}
                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                            <MenuItem value="Suspended">Suspended</MenuItem>
                                            <MenuItem value="Expired">Expired</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                        {/* Upload License Document */}
                        <Grid item xs={12}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500, color: 'grey.700' }}
                            >
                                Upload License Document {newLicense.licenseType !== '69aacb7782693bdedf50db43' ? '*' : ''}
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed #d1d5db',
                                    borderRadius: 2,
                                    p: 4,
                                    textAlign: 'center',
                                    bgcolor: 'white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        borderColor: '#2E7DAF',
                                        bgcolor: '#f8f9ff',
                                    },
                                }}
                                onClick={() =>
                                    document.getElementById('new-license-upload')?.click()
                                }
                            >
                                <CloudUploadIcon
                                    sx={{
                                        fontSize: 48,
                                        color: '#9ca3af',
                                        mb: 2,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'grey.600',
                                        mb: 1,
                                        fontWeight: 500,
                                    }}
                                >
                                    Drag and drop your license document, or click to browse
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'grey.500',
                                        display: 'block',
                                        mb: 2,
                                    }}
                                >
                                    Supported formats: PDF, JPG, PNG (Max 10MB)
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#2E7DAF',
                                        fontWeight: 500,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                >
                                    browse
                                </Typography>
                                <input
                                    id="new-license-upload"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    style={{ display: 'none' }}
                                    onChange={e =>
                                        handleNewLicenseFileUpload(e.target.files?.[0] || null)
                                    }
                                />
                                {!!newLicense.licenseFileUrl && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'grey.600',
                                            display: 'block',
                                            mt: 1,
                                        }}
                                    >
                                        ✓ Document Uploaded
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseAddLicenseDialog}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddLicense}
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                    >
                        Add License
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Delete License
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete the license "
                        <strong>
                            {licenseToDelete?.licenseType || licenseToDelete?.licenseNpi}
                        </strong>
                        "? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        sx={{ textTransform: 'none' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

export default LicenseManagementPage